import { Flex, Text } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useGenerateDataExport } from 'src/data-export/hooks/useGenerateDataExport';
import { DataExportStepProps } from '../types';

type Props = DataExportStepProps & {
  onSubmittingChange?: (isSubmitting: boolean) => void;
};

export function DataExportProcessingStep({ data, setData, onSubmittingChange }: Props) {
  const { generateDataExport } = useGenerateDataExport();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const didSubmitRef = useRef(false);

  useEffect(() => {
    onSubmittingChange?.(isSubmitting);
  }, [isSubmitting, onSubmittingChange]);

  const submitDataExport = async () => {
    if (!data.configId) {
      notifyAndLogError(
        'No configuration selected',
        'Please select a configuration and try again.',
        new Error('Missing config'),
      );
      return;
    }

    if (data.isFamily && data.family) {
      setIsSubmitting(true);
      try {
        const response = await generateDataExport('family', data.family.id, {
          config_id: data.configId,
          include_sensitive_fields: data.includeSensitiveFields,
          export_format: data.exportFormat,
        });
        setData(prev => ({ ...prev, fileOwnerClientId: response.data.fileOwnerClientId }));
      } catch (error) {
        notifyAndLogError('Failed to generate data export', 'Please try again.', error, {
          familyId: data.family.id,
        });
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (!data.isFamily && data.client) {
      setIsSubmitting(true);
      try {
        const response = await generateDataExport('client', data.client.id, {
          config_id: data.configId,
          include_sensitive_fields: data.includeSensitiveFields,
          export_format: data.exportFormat,
        });
        setData(prev => ({ ...prev, fileOwnerClientId: response.data.fileOwnerClientId }));
      } catch (error) {
        notifyAndLogError('Failed to generate data export', 'Please try again.', error, {
          clientId: data.client.id,
        });
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    notifyAndLogError(
      'No client or family selected',
      'Please select a client or family and try again.',
      new Error('Missing client or family'),
    );
  };

  useEffect(() => {
    if (didSubmitRef.current || data.fileOwnerClientId) return;
    const hasValidSelection = data.isFamily ? !!data.family?.id : !!data.client?.id;
    if (!hasValidSelection) return;
    didSubmitRef.current = true;
    void submitDataExport();
  }, [data]);

  return (
    <Flex direction="column" flex={1} h="100%" justify="center" align="center">
      <Text c="gray.10" fw={FONT_WEIGHTS.bold} size="displayMd">
        Fine tuning...
      </Text>
    </Flex>
  );
}
